import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { Notification } from 'react-notification'
import useTranslation from 'next-translate/useTranslation'
import { Preloader } from 'SRC/ui/Preloader'
import Router from 'next/router'
import { connect } from 'react-redux'
import { VehiclPartOrdersApi } from 'SRC/modules/vehicleParts/orders/api'
import { getUserInfo } from 'SRC/modules/users/Profile/selectors'
import { getIsAuthorized } from 'SRC/modules/users/Auth/selectors'
import { Sidebar as VehiclePartSidebar } from '../Sidebar'
import { getOrderForm } from '../../selectors'
import { getIsMobileDevice } from 'SRC/modules/common/selectors'
import { SimilarProducts } from '../SimilarProducts'
import { ProductSummary } from '../ProductSummary'
import { PageHeader as VehiclePartPageHeader } from '../PageHeader'
import { ShippingAndPaymentInfo } from '../ShippingAndPaymentInfo'
import { Specifications } from '../Specifications'
import { Installments } from '../Installments'
import { InstallmentInfo } from 'SRC/modules/vehicleParts/components/InstallmentInfo'
import { Delivery } from '../Delivery'
import { getVehiclePart } from '../../selectors'

import translations from '../../../../../../public/data/translations.json'

const Container = ({ vehiclePart, orderForm, isAuthorized, userInfo, isMobileDevice }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [notification, setNotification] = useState('')

  const { t, lang } = useTranslation('common')

  const locale = lang?.toUpperCase() || 'ME'

  useEffect(() => {
    setNotification('')
    setIsSubmitting(false)
  }, [vehiclePart?.ean])

  if (!vehiclePart) return null

  const submit = async () => {
    if (isSubmitting) return

    setIsSubmitting(true)

    const api = new VehiclPartOrdersApi()

    const payload = {
      productCode: vehiclePart.product_code,
      ean: vehiclePart.ean,
      quantity: 1,
      locale
    }

    if (isAuthorized && userInfo) {
      payload.isLoggedIn = true
      payload.user = {
        username: userInfo.username,
        name: userInfo.name,
      }

      if (userInfo.surname) payload.user.name += ` ${userInfo.surname}`

      if (Array.isArray(userInfo.phones) && userInfo.phones.length) {
        payload.user.phones = userInfo.phones.map(phone => ({
          phone: phone.phone,
          visible: phone.visible
        }))
      }
    }

    const response = await api.createOrder(payload)

    if (response.error) {
      setIsSubmitting(false)
      return setNotification(response.error)
    }

    if (response.status === "success" && response.orderNumber) {
      setNotification(response.message || t('vehicle_parts_request_success_message'))

      const as = `/${translations.noviDjeloviUrl[locale]}/order/${response.orderNumber}`
      const href = {
        pathname: '/vehiclePartOrder',
        query: {
          orderNumber: response.orderNumber, locale: lang
        }
      }

      Router.push(href, as)
    } else {
      setIsSubmitting(false)
    }
  }

  const specifications = Array.isArray(vehiclePart?.product_description) ? vehiclePart.product_description : []

  return (
    <>
      <VehiclePartPageHeader />

      <div className='vehicle-part-container'>
        <div className='vehicle-part-content'>
          <div className='vehicle-part-content-main'>
            <ProductSummary submit={submit} />

            {vehiclePart?.installment_info?.enabled &&
              Array.isArray(vehiclePart.installment_info.options) &&
              vehiclePart.installment_info.options.length ? (
              <InstallmentInfo installmentInfo={vehiclePart.installment_info} />
            ) : null}

            <ShippingAndPaymentInfo />

            <Delivery />

            {/* {Array.isArray(vehiclePart?.installments) && vehiclePart.installments.length && vehiclePart?.installment_enabled ? (
              <Installments
                installments={vehiclePart.installments}
              />
            ) : null} */}

            <Specifications specifications={specifications} />
          </div>

          <VehiclePartSidebar />
        </div>

        <SimilarProducts />

        <Notification
          isActive={!!notification}
          message={notification}
          action={t('Sakrij')}
          onClick={() => { setNotification('') }}
        />

        {isSubmitting ? <Preloader /> : null}
      </div>
    </>
  )
}

Container.propTypes = {
  vehiclePart: PropTypes.shape({
    name: PropTypes.string.isRequired,
    manufacturer: PropTypes.string.isRequired,
    product_code: PropTypes.string.isRequired,
    ean: PropTypes.string.isRequired,
    vehicle_type: PropTypes.string.isRequired,
    year_from: PropTypes.string.isRequired,
    year_to: PropTypes.string.isRequired,
    kw: PropTypes.string.isRequired,
    pm: PropTypes.string.isRequired,
    cc: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    car_brand: PropTypes.string.isRequired,
    car_model: PropTypes.string.isRequired,
    car_type: PropTypes.string.isRequired,
    pdf_path: PropTypes.string.isRequired,
    img_path: PropTypes.string.isRequired,
    alternative: PropTypes.shape({
      Izvedba: PropTypes.string.isRequired,
      Napon: PropTypes.string.isRequired,
      Tehnika: PropTypes.string.isRequired,
      Ukupna: PropTypes.string.isRequired,
      Ugradna: PropTypes.string.isRequired,
      Dimenzija: PropTypes.string.isRequired,
      Širina: PropTypes.string.isRequired,
      Obrtni: PropTypes.string.isRequired,
      Korak: PropTypes.string.isRequired,
    }).isRequired,
    product_description: PropTypes.shape({
      Izvedba: PropTypes.string.isRequired,
      Napon: PropTypes.string.isRequired,
      Tehnika: PropTypes.string.isRequired,
      Ukupna: PropTypes.string.isRequired,
      Ugradna: PropTypes.string.isRequired,
      Dimenzija: PropTypes.string.isRequired,
      Širina: PropTypes.string.isRequired,
      Obrtni: PropTypes.string.isRequired,
      Korak: PropTypes.string.isRequired,
    }).isRequired,
    application: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    discounted_price: PropTypes.number.isRequired,
    discount: PropTypes.number.isRequired,
    show_discount: PropTypes.bool.isRequired,
    promotion: PropTypes.bool.isRequired,
    published: PropTypes.bool.isRequired,
    tire_diameter_slug: PropTypes.string,
    tire_width_slug: PropTypes.string,
    tire_height_slug: PropTypes.string,
    tire_season_slug: PropTypes.string,
  }),
  isMobileDevice: PropTypes.bool.isRequired
}

Container.displayName = 'VehiclePartDetailContainer'

const mapStateToProps = state => {
  return {
    vehiclePart: getVehiclePart(state),
    orderForm: getOrderForm(state),
    userInfo: getUserInfo(state),
    isAuthorized: getIsAuthorized(state),
    isMobileDevice: getIsMobileDevice(state)
  }
}

export default connect(mapStateToProps)(Container)
